// "Origins" (module 10 U260; DEAL-WORK-BRIEF-2026-10-04.md section 4; REQ-2026-10-05-16, the Deal work lane's Origins): at
// the top of a deal's history page, how the deal started: the first message on it, the show it was met at with the show
// meeting, the SBC notes it came from (who met, the terms heard, the route to live, what the client said), and the HubSpot
// deal with its stage and dates as HubSpot left them. What the reader may not read is not there (R1); a deal with none of
// it draws nothing. Mounted in DealHistory.tsx under "This looks like the same deal", agreed with the P8 session.
import { ActionButton } from '../parts/index.js';
import { useEffect, useState } from 'react';
import { dayMonth, dayMonthYear } from '../day.js';
import { opensPath } from '../deals/live-words.js';
import { dealWorkApi, type OriginMessage, type Origins as O } from './api.js';
import './dealwork.css';

const sheetDay = (d: string) => new Date(`${d}T12:00:00Z`);

/** "29 Sep to 1 Oct"; one day as itself. */
export function showDays(startsOn: string | null, endsOn: string | null): string | null {
  if (!startsOn) return null;
  const a = dayMonth(sheetDay(startsOn), 'utc');
  if (!endsOn || endsOn === startsOn) return a;
  return `${a} to ${dayMonth(sheetDay(endsOn), 'utc')}`;
}

/** "From HubSpot: at Proposal when it was put aside on 4 Oct, closing 30 Nov 2026; brought here on 5 Oct by Mike Cobain." */
export function hubspotWords(h: NonNullable<O['hubspot']>): string {
  const at = h.stage ? `at ${h.stage}` : 'with no stage';
  const when = h.self ? ' as it was imported' : h.putAsideAt ? ` when it was put aside on ${dayMonth(new Date(h.putAsideAt))}` : '';
  const close = h.closeOn ? `, closing ${dayMonthYear(sheetDay(h.closeOn), 'utc')}` : '';
  const joined = h.joinedAt ? `; brought here on ${dayMonth(new Date(h.joinedAt))}${h.joinedBy ? ` by ${h.joinedBy}` : ''}` : '';
  return `${at}${when}${close}${joined}.`;
}

function Line({ m, store, open }: { m: OriginMessage; store: string; open: ((path: string) => void) | undefined }) {
  const path = opensPath(m.signalId, m.opens, store);
  const words = m.subject ?? 'Open it';
  return path && open ? <ActionButton reading type="button" className="hx-link dw-olink" onClick={() => open(path)}>{words}</ActionButton> : <span>{m.subject ?? ''}</span>;
}

/** `stamp` changes when the page's history is read again (a join filed mail under the deal), so the origins are read again too. */
export function Origins({ store, dealId, open, stamp }: { store: string; dealId: string; open?: (path: string) => void; stamp?: number }) {
  const [o, setO] = useState<O | null>(null);
  useEffect(() => {
    let live = true;
    dealWorkApi.origins(store, dealId).then((r) => { if (live) setO(r.origins); }).catch(() => undefined);
    return () => { live = false; };
  }, [store, dealId, stamp]);
  if (!o || (!o.first && !o.show && !o.notes && !o.hubspot)) return null;
  const days = o.show ? showDays(o.show.startsOn, o.show.endsOn) : null;
  return (
    <section className="lv-panel dw-origins" aria-label="Origins">
      <div className="lv-ph"><h2>Origins</h2><span className="hx-meta">how the deal started</span></div>
      <dl>
        {o.first ? (
          <div><dt>First message</dt><dd><span className="d">{dayMonth(new Date(o.first.at))}</span> {o.first.from ? `${o.first.from}: ` : ''}<Line m={o.first} store={store} open={open} /></dd></div>
        ) : null}
        {o.show ? (
          <div><dt>Met at</dt><dd>{o.show.name}{days ? `, ${days}` : ''}{o.notes?.metBy ? `, by ${o.notes.metBy}` : ''}</dd></div>
        ) : null}
        {o.show?.meeting ? (
          <div><dt>The show meeting</dt><dd><span className="d">{dayMonth(new Date(o.show.meeting.at))}</span> <Line m={o.show.meeting} store={store} open={open} /></dd></div>
        ) : null}
        {o.notes?.said ? <div><dt>{o.notes.ref ? `The SBC notes (${o.notes.ref})` : 'The SBC notes'}</dt><dd>{o.notes.said}</dd></div> : null}
        {o.notes?.terms ? <div><dt>Terms heard</dt><dd>{o.notes.terms}</dd></div> : null}
        {o.notes?.route ? <div><dt>Route to live</dt><dd>{o.notes.route}</dd></div> : null}
        {!o.show && o.notes?.metBy ? <div><dt>Met by</dt><dd>{o.notes.metBy}</dd></div> : null}
        {o.hubspot ? <div><dt>{o.hubspot.self ? 'From HubSpot' : `From HubSpot: ${o.hubspot.title}`}</dt><dd>{hubspotWords(o.hubspot)}</dd></div> : null}
      </dl>
    </section>
  );
}
